import { __spreadArrays } from "tslib";
import { Platform, debounce } from '@tyler-components-web/core';
import { randomChars } from '../core';
import { isSupportedTimeFormat, TWENTY_FOUR_HOUR_TIME_REGEX, tryCoerceTimeString } from '../core/time-utils';
import { TIME_PICKER_CONSTANTS } from './time-picker-constants';
import { ListDropdownType } from '../list-dropdown/list-dropdown-constants';
import { timeStringToMillis, millisToTimeString, minutesToMillis, getCurrentTimeOfDayMillis, millisToMinutes, stripSecondsFromMillis } from './time-picker-utils';
import { POPUP_DIALOG_WIDTH_THRESHOLD } from '../constants';
var TimePickerFoundation = /** @class */ (function () {
    function TimePickerFoundation(_adapter) {
        var _this = this;
        this._adapter = _adapter;
        // State vars
        this._value = null;
        this._masked = true;
        this._use24HourTime = false;
        this._showMaskFormat = false;
        this._useCharMask = false;
        this._min = null;
        this._max = null;
        this._restrictedTimes = [];
        this._startTime = null;
        this._step = TIME_PICKER_CONSTANTS.numbers.DEFAULT_MINUTE_STEP;
        this._allowInput = true;
        this._open = false;
        this._allowSeconds = false;
        this._allowInvalidTime = false;
        this._showNow = false;
        this._customOptions = [];
        this._disabled = false;
        this._popupClasses = [];
        this._dialogWidthThreshold = POPUP_DIALOG_WIDTH_THRESHOLD;
        this._allowDropdown = true;
        // Internal state vars
        this._isInitialized = false;
        this._identifier = randomChars(); // This is a unique identifier for this instance of the time picker (used for a11y purposes)
        // Set up listeners
        this._inputListener = function (evt) { return _this._onInput(evt); };
        this._inputKeydownListener = function (evt) { return _this._onInputKeydown(evt); };
        this._toggleMousedownListener = function (evt) { return _this._onToggleMousedown(evt); };
        this._inputFocusListener = function (evt) { return _this._onInputFocus(evt); };
        this._inputBlurListener = function (evt) { return _this._onInputBlur(evt); };
        this._inputMousedownListener = function (evt) { return _this._onInputMousedown(evt); };
    }
    TimePickerFoundation.prototype.initialize = function () {
        this._adapter.initialize();
        // We require an input element to be a child of this component
        if (!this._adapter.hasInputElement()) {
            throw new Error('Unable to locate <input> element to attach to.');
        }
        this._adapter.initializeAccessibility(this._identifier);
        // Detect if a value already exists in the input and set our values based on that
        if (!this._value) {
            var inputValue = this._adapter.getInputValue();
            this._setValue(this._convertTimeStringToMillis(inputValue, this._use24HourTime, this._allowSeconds));
        }
        // Attach required listeners
        this._adapter.addInputListener('focus', this._inputFocusListener);
        this._adapter.addInputListener('blur', this._inputBlurListener);
        this._adapter.addInputListener('keydown', this._inputKeydownListener);
        // Ensure our input has the correct time format (make sure not to emit input events manually at this point)
        this._formatInputValue(false);
        // Apply our state
        if (typeof this._value === 'number') {
            this._applyValue(this._value, false);
        }
        this._applyAllowInput();
        this._applyAllowDropdown();
        this._applyDisabled();
        this._applyMask();
        this._isInitialized = true;
    };
    TimePickerFoundation.prototype.disconnect = function () {
        this._isInitialized = false;
        // Remove listeners
        this._adapter.removeInputListener('input', this._inputListener);
        this._adapter.removeInputListener('focus', this._inputFocusListener);
        this._adapter.removeInputListener('blur', this._inputBlurListener);
        this._adapter.removeToggleListener('mousedown', this._toggleMousedownListener);
        this._adapter.removeInputListener('keydown', this._inputKeydownListener);
        // If we are using an input mask, let's destroy that now
        if (this._masked) {
            this._adapter.destroyMask();
        }
        // Ensure we remove our dropdown
        this._closeDropdown();
        // Cleanup any resources used in the adapter
        this._adapter.destroy();
    };
    /** Called when the input mask changes or the `input` event occurs on our `<input>` element. */
    TimePickerFoundation.prototype._onInput = function (evt) {
        this._handleInput(this._adapter.getInputValue());
    };
    TimePickerFoundation.prototype._onInputKeydown = function (evt) {
        if (evt.shiftKey) {
            switch (evt.code) {
                case 'Backspace':
                case 'Delete':
                    evt.preventDefault();
                    var result = this._trySetValue(null);
                    if (result) {
                        this._formatInputValue();
                    }
                    return;
            }
        }
        switch (evt.code) {
            case 'Esc':
            case 'Escape':
                if (this._open) {
                    evt.preventDefault();
                    this._closeDropdown(true);
                }
                break;
            case 'Down':
            case 'ArrowDown':
                if (this._allowDropdown) {
                    evt.preventDefault();
                    if (!this._open) {
                        this._openDropdown();
                        // TODO: Should we cycle the hours, minutes, seconds, or meridiem where the cursor is instead of opening the dropdown?
                    }
                    else {
                        this._adapter.propagateKey(evt.code);
                    }
                }
                break;
            case 'Up':
            case 'ArrowUp':
                if (this._allowDropdown) {
                    evt.preventDefault();
                    if (this._open) {
                        this._adapter.propagateKey(evt.code);
                    }
                    else {
                        // TODO: cycle the hours, minutes, seconds, or meridiem where the cursor is
                    }
                }
                break;
            case 'Enter':
            case 'Home':
            case 'End':
                evt.preventDefault();
                this._adapter.propagateKey(evt.code);
                break;
            case 'KeyN':
                evt.preventDefault();
                var nowMillis = getCurrentTimeOfDayMillis(this._allowSeconds);
                if (this._value !== nowMillis) {
                    var timeString = millisToTimeString(nowMillis, true, this._allowSeconds);
                    var canContinue = this._emitChangeEvent(timeString);
                    if (canContinue) {
                        this._applyValue(nowMillis);
                        this._selectInputText();
                    }
                }
                break;
        }
    };
    TimePickerFoundation.prototype._onToggleMousedown = function (evt) {
        var _this = this;
        if (this._disabled || !this.allowDropdown) {
            return;
        }
        evt.stopPropagation();
        if (this._adapter.isInputDisabled()) {
            return;
        }
        evt.preventDefault();
        if (this._open) {
            this._closeDropdown(true);
        }
        else {
            if (!Platform.isMobile) {
                this._adapter.tryFocusInput();
            }
            else {
                this._adapter.tryBlurInput();
            }
            if (Platform.TRIDENT) {
                window.requestAnimationFrame(function () { return _this._openDropdown(); });
            }
            else {
                this._openDropdown();
            }
        }
    };
    TimePickerFoundation.prototype._onInputFocus = function (evt) {
        if (this._allowInput) {
            this._adapter.selectInputText();
        }
    };
    TimePickerFoundation.prototype._onInputBlur = function (evt) {
        this._formatInputValue();
        if (this._open && !this._adapter.isInputFocused()) {
            this._closeDropdown(true);
        }
    };
    TimePickerFoundation.prototype._onInputMousedown = function (evt) {
        var _this = this;
        if (!this._allowInput && !this._open) {
            this._openDropdown();
            this._adapter.tryFocusInput();
            window.requestAnimationFrame(function () { return _this._adapter.selectInputText(); });
        }
    };
    TimePickerFoundation.prototype._applyDisabled = function () {
        this._adapter.setDisabled(this._disabled);
    };
    TimePickerFoundation.prototype._applyMask = function () {
        var _this = this;
        if (this._masked && this._allowInput) {
            var options = {
                showMaskFormat: this._showMaskFormat,
                use24HourTime: this._use24HourTime,
                showSeconds: this._allowSeconds,
                prepareCallback: this._prepareMaskCallback,
                onChange: debounce(function (value) { return _this._handleInput(value); }, 0, true) // debounce this to avoid multiple callbacks for same value
            };
            this._adapter.initializeMask(options);
        }
        else {
            this._adapter.destroyMask();
            this._formatInputValue();
        }
    };
    TimePickerFoundation.prototype._applyAllowInput = function () {
        this._adapter.setInputReadonly(!this._allowInput);
        if (this._allowInput) {
            this._adapter.removeInputListener('mousedown', this._inputMousedownListener);
            // If not using an input mask we need to attach our own `input` listener, otherwise
            // we use the mask to notify us of changes
            if (!this._masked) {
                this._adapter.addInputListener('input', this._inputListener);
            }
        }
        else {
            this._adapter.addInputListener('mousedown', this._inputMousedownListener);
            this._adapter.removeInputListener('input', this._inputListener);
        }
    };
    TimePickerFoundation.prototype._applyAllowDropdown = function () {
        this._adapter.setToggleDisabled(!this._allowDropdown);
        if (this._allowDropdown) {
            this._adapter.removeToggleListener('mousedown', this._toggleMousedownListener);
            this._adapter.tryCreateToggle();
            this._adapter.addToggleListener('mousedown', this._toggleMousedownListener);
        }
        else if (this._open) {
            this._closeDropdown(true);
        }
    };
    TimePickerFoundation.prototype._applyAllowSeconds = function () {
        var originalValue = this._value;
        if (!this._allowSeconds) {
            // If we are not using seconds, we need to remove our seconds from the value
            if (typeof this._value !== 'number') {
                return;
            }
            this._setValue(stripSecondsFromMillis(this._value));
            if (this._isInitialized && originalValue !== this._value) {
                // We need to emit a change event with our new value which does not include the seconds
                var timeString = millisToTimeString(this._value, true, false);
                this._emitChangeEvent(timeString);
            }
        }
        else if (this._isInitialized && originalValue !== this._value) {
            // We need to emit a change event with our new value which includes the seconds
            var timeString = millisToTimeString(this._value, true, true);
            this._emitChangeEvent(timeString);
        }
    };
    TimePickerFoundation.prototype._handleInput = function (value) {
        var rawValue = value;
        if (!this._allowInput) {
            return;
        }
        if (this._open) {
            this._closeDropdown(true);
        }
        // Let's attempt to coerce our time string into a known format to help with ease of entry
        value = tryCoerceTimeString(value, this._use24HourTime, this._allowSeconds);
        // If we were provided a custom coercion callback then we need to call that now to perform any further coercion by the consumer
        if (typeof this._coercionCallback === 'function') {
            value = this._coercionCallback.call(null, rawValue, value, this._allowSeconds);
        }
        // Validate the time format and make sure it's supported
        if (!this._isValidTimeFormat(value)) {
            value = '';
        }
        // Convert the time string to milliseconds
        var millis;
        if (!this._masked && typeof this._parseCallback === 'function') {
            millis = this._parseCallback.call(null, value);
        }
        else {
            millis = this._convertTimeStringToMillis(value, this._use24HourTime, this._allowSeconds);
        }
        // Validate that our time can be chosen as a valid value
        millis = this._validateMillis(millis);
        // Update our value
        this._trySetValue(millis);
    };
    TimePickerFoundation.prototype._emitChangeEvent = function (value, force) {
        if (force === void 0) { force = false; }
        return this._adapter.emitHostEvent(TIME_PICKER_CONSTANTS.events.CHANGE, value, true, !force);
    };
    TimePickerFoundation.prototype._trySetValue = function (millis) {
        // If our value hasn't changed, we can just return
        if (millis === this._value) {
            return false;
        }
        // Convert our milliseconds to a 24-hour time string to use as our normalized value
        var timeString = millisToTimeString(millis, true, this._allowSeconds);
        // If we are using an input mask, we need to dispatch our custom input event to let consumers know input happened (before our change event)
        if (this._masked) {
            this._adapter.emitInputEvent(TIME_PICKER_CONSTANTS.events.INPUT, timeString);
        }
        // Only emit our change event if the value is different
        if (this._value !== millis) {
            var canContinue = this._emitChangeEvent(timeString);
            if (canContinue) {
                this._setValue(millis);
                return true;
            }
        }
        return false;
    };
    TimePickerFoundation.prototype._setValue = function (value) {
        this._value = this._normalizeTimeValue(value);
    };
    TimePickerFoundation.prototype._validateMillis = function (millis) {
        // Trap for min/max validation
        if (typeof millis === 'number') {
            var isBelowMin = typeof this._min === 'number' && millis < this._min;
            var isAboveMax = typeof this._max === 'number' && millis > this._max;
            if (isBelowMin || isAboveMax) {
                millis = null;
            }
        }
        // Trap for restricted times
        if (typeof millis === 'number' && this._restrictedTimes.length) {
            if (this._restrictedTimes.includes(millis)) {
                millis = null;
            }
        }
        return millis;
    };
    TimePickerFoundation.prototype._isValidTimeFormat = function (value) {
        if (!this._masked && typeof this._validationCallback === 'function') {
            return this._validationCallback.call(this, value);
        }
        return isSupportedTimeFormat(value);
    };
    TimePickerFoundation.prototype._isValidInputValue = function (value) {
        return TWENTY_FOUR_HOUR_TIME_REGEX.test(value);
    };
    TimePickerFoundation.prototype._onSelect = function (value) {
        this._closeDropdown(true);
        // Check if the "Now" option was selected
        if (!value.isCustom && value.metadata === 'now') {
            value.time = getCurrentTimeOfDayMillis(this._allowSeconds);
        }
        // Check if this is a custom option, and then call the provided callback for it
        if (value.isCustom) {
            if (typeof value.customCallback === 'function') {
                var result = value.customCallback.call(null, value.metadata);
                if (typeof result !== 'number') {
                    throw new Error('Custom options must provide a time of day value in milliseconds.');
                }
                value.time = result;
            }
            else {
                throw new Error('You must implement a `toMilliseconds` callback that returns the time value to use for this custom option.');
            }
        }
        // Make sure the time is different than what is already populated or we can just return
        if (this._value === value.time) {
            return;
        }
        // Emit the change event
        var timeString = millisToTimeString(value.time, true, this._allowSeconds);
        var canContinue = this._emitChangeEvent(timeString);
        if (!canContinue) {
            return;
        }
        // This will update our state and the UI at once
        this._applyValue(value.time);
        // Select full input text
        this._selectInputText();
    };
    TimePickerFoundation.prototype._selectInputText = function () {
        var _this = this;
        // We need to select the text of the input in the next cycle to allow time for the UI to update
        // with the selected text (assuming it still has focus)
        window.requestAnimationFrame(function () {
            window.requestAnimationFrame(function () {
                if (_this._adapter.isInputFocused()) {
                    _this._adapter.selectInputText();
                }
            });
        });
    };
    TimePickerFoundation.prototype._applyValue = function (value, emitEvents) {
        if (emitEvents === void 0) { emitEvents = true; }
        this._setValue(value);
        var formattedValue = this._formatValue(this._value);
        if (this._adapter.getInputValue() !== formattedValue) {
            this._adapter.setInputValue(formattedValue, emitEvents);
        }
    };
    TimePickerFoundation.prototype._normalizeTimeValue = function (value) {
        if (value === null || value === undefined) {
            return null;
        }
        else if (value < 0) {
            return 0;
        }
        else if (value > TIME_PICKER_CONSTANTS.numbers.MAX_DAY_MILLIS) {
            return TIME_PICKER_CONSTANTS.numbers.MAX_DAY_MILLIS;
        }
        return value;
    };
    TimePickerFoundation.prototype._openDropdown = function () {
        var _this = this;
        if (!this.allowDropdown) {
            return;
        }
        this._formatInputValue();
        this._open = true;
        this._adapter.setHostAttribute(TIME_PICKER_CONSTANTS.attributes.OPEN);
        var options = this._generateTimeOptions();
        var selectableOptions = options.filter(function (o) { return !o.divider && !o.disabled; });
        var selectedValues = [];
        var activeStartIndex;
        // Find closest match in list of time options and activate/select it
        if (this._value !== null) {
            var optionIndex = this._findClosestOptionIndex(this._value, selectableOptions);
            if (optionIndex >= 0) {
                var isExactMatch = selectableOptions[optionIndex].value.time === this._value;
                if (isExactMatch) {
                    selectedValues = [selectableOptions[optionIndex].value];
                }
                else {
                    activeStartIndex = optionIndex;
                }
            }
        }
        else if (typeof this._startTime === 'number') {
            var optionIndex = this._findClosestOptionIndex(this._startTime, selectableOptions);
            if (optionIndex >= 0 && optionIndex < selectableOptions.length) {
                activeStartIndex = optionIndex;
            }
        }
        var config = {
            id: "tcw-time-picker-" + this._identifier,
            selectedValues: selectedValues,
            syncWidth: true,
            highlightFirst: true,
            activeStartIndex: activeStartIndex,
            popupClasses: this._popupClasses,
            popupStatic: true,
            type: ListDropdownType.Standard,
            options: options,
            dialogTitle: 'Choose a time',
            dialogWidthThreshold: this._dialogWidthThreshold,
            selectCallback: function (value) { return _this._onSelect(value); },
            closeCallback: function () { return _this._closeDropdown(true); },
            activeChangeCallback: function (id) { return _this._adapter.setActiveDescendant(id); },
            targetWidthCallback: function () { return _this._adapter.getTargetElementWidth(_this._popupTarget); }
        };
        this._adapter.attachDropdown(config);
        this._adapter.emitHostEvent(TIME_PICKER_CONSTANTS.events.OPEN, true, false);
    };
    TimePickerFoundation.prototype._closeDropdown = function (emitCloseEvent) {
        if (emitCloseEvent === void 0) { emitCloseEvent = false; }
        this._open = false;
        this._adapter.removeHostAttribute(TIME_PICKER_CONSTANTS.attributes.OPEN);
        this._adapter.detachDropdown();
        if (emitCloseEvent) {
            this._adapter.emitHostEvent(TIME_PICKER_CONSTANTS.events.CLOSE, true, false);
        }
    };
    TimePickerFoundation.prototype._findClosestOptionIndex = function (value, options) {
        var closestItem = options.reduce(function (prev, curr) {
            return Math.abs((curr.value.time || 0) - value) < Math.abs((prev.value.time || 0) - value) ? curr : prev;
        });
        return options.indexOf(closestItem);
    };
    TimePickerFoundation.prototype._formatInputValue = function (emitEvents) {
        if (emitEvents === void 0) { emitEvents = true; }
        var inputValue = this._adapter.getInputValue();
        // If we allow invalid times, we can leave the input value as-is (only when unmasked)
        if (this._allowInvalidTime && !this._masked && inputValue && !this._value) {
            return;
        }
        var formattedValue = this._formatValue(this._value);
        if (inputValue !== formattedValue) {
            this._adapter.setInputValue(formattedValue, emitEvents);
        }
    };
    TimePickerFoundation.prototype._generateTimeOptions = function () {
        var minMinutes = this._min !== null ? Math.max(millisToMinutes(this._min), 0) : 0;
        var maxMinutes = this._max !== null ? Math.min(millisToMinutes(this._max), TIME_PICKER_CONSTANTS.numbers.MAX_DAY_MINUTES) : TIME_PICKER_CONSTANTS.numbers.MAX_DAY_MINUTES;
        var minuteStep = this._step;
        var times = [];
        var leadingOptions = [];
        for (var totalMinutes = minMinutes; totalMinutes <= maxMinutes; totalMinutes += minuteStep) {
            if (totalMinutes === TIME_PICKER_CONSTANTS.numbers.MAX_DAY_MINUTES) {
                break;
            }
            var millis = minutesToMillis(totalMinutes);
            var disabled = this._restrictedTimes.includes(millis);
            var label = millisToTimeString(millis, this._use24HourTime, false) || '';
            var value = { time: millis };
            times.push({ label: label, value: value, disabled: disabled });
        }
        // Add divider between AM/PM times
        var firstPmIndex = times.findIndex(function (t) { return t.value.time / 1000 / 60 >= 720; });
        if (firstPmIndex >= 0 && firstPmIndex < times.length - 1) {
            times.splice(firstPmIndex, 0, { label: '', value: null, divider: true });
        }
        // Check if we need to prepend a "Now" option
        if (this._showNow) {
            var value = { time: null, metadata: 'now' };
            leadingOptions.push({ label: 'Now', value: value });
        }
        // Check for any custom provided options to prepend
        if (Array.isArray(this._customOptions) && this._customOptions.length) {
            var options = this._customOptions.map(function (o) {
                var value = { time: null, metadata: o.value, isCustom: true, customCallback: o.toMilliseconds };
                return { label: o.label, value: value };
            });
            leadingOptions = __spreadArrays(leadingOptions, options);
        }
        // Append all leading options
        if (leadingOptions.length) {
            times.splice(0, 0, { label: '', value: null, divider: true });
            leadingOptions.forEach(function (o, index) { return times.splice(index, 0, o); });
        }
        return times;
    };
    TimePickerFoundation.prototype._convertTimeStringToMillis = function (value, use24HourTime, allowSeconds) {
        if (!value || !this._isValidTimeFormat(value)) {
            value = '';
        }
        return timeStringToMillis(value, use24HourTime, allowSeconds);
    };
    TimePickerFoundation.prototype._formatValue = function (value) {
        if (!this._masked && typeof this._formatCallback === 'function') {
            return this._formatCallback.call(null, value, this._use24HourTime, this._allowSeconds);
        }
        return millisToTimeString(this._value, this._use24HourTime, this._allowSeconds) || '';
    };
    TimePickerFoundation.prototype._warnInvalidFormat = function (value) {
        console.warn("The specified value \"" + value + "\" does not conform to the required format. The format is \"HH:mm\", \"HH:mm:ss\" where HH is 00-23, mm is 00-59, and ss is 00-59.");
    };
    Object.defineProperty(TimePickerFoundation.prototype, "open", {
        get: function () {
            return this._open;
        },
        set: function (value) {
            if (this._open !== value) {
                if (this._isInitialized) {
                    this._open = value;
                    if (this._open) {
                        this._openDropdown();
                    }
                    else {
                        this._closeDropdown();
                    }
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TimePickerFoundation.prototype, "masked", {
        get: function () {
            return this._masked;
        },
        set: function (value) {
            if (this._masked !== value) {
                this._masked = value;
                if (this._isInitialized) {
                    if (this._masked) {
                        this._adapter.removeInputListener('input', this._inputListener);
                        this._applyMask();
                    }
                    else {
                        this._adapter.destroyMask();
                        this._formatInputValue();
                        this._adapter.addInputListener('input', this._inputListener);
                    }
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TimePickerFoundation.prototype, "showMaskFormat", {
        get: function () {
            return this._showMaskFormat;
        },
        set: function (value) {
            if (this._showMaskFormat !== value) {
                this._showMaskFormat = value;
                if (this._isInitialized) {
                    this._applyMask();
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TimePickerFoundation.prototype, "useCharMask", {
        get: function () {
            return this._useCharMask;
        },
        set: function (value) {
            if (this._useCharMask !== value) {
                this._useCharMask = value;
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TimePickerFoundation.prototype, "allowSeconds", {
        get: function () {
            return this._allowSeconds;
        },
        set: function (value) {
            if (this._allowSeconds !== value) {
                this._allowSeconds = !!value;
                this._applyAllowSeconds();
                if (this._isInitialized) {
                    this._applyMask();
                    this._formatInputValue();
                }
                this._adapter.setHostAttribute(TIME_PICKER_CONSTANTS.attributes.ALLOW_SECONDS, "" + !!value);
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TimePickerFoundation.prototype, "use24HourTime", {
        get: function () {
            return this._use24HourTime;
        },
        set: function (value) {
            if (this._use24HourTime !== value) {
                this._use24HourTime = !!value;
                if (this._isInitialized) {
                    this._adapter.destroyMask();
                    this._formatInputValue();
                    this._applyMask();
                }
                this._adapter.setHostAttribute(TIME_PICKER_CONSTANTS.attributes.USE_24_HOUR_TIME, "" + !!value);
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TimePickerFoundation.prototype, "allowInvalidTime", {
        get: function () {
            return this._allowInvalidTime;
        },
        set: function (value) {
            if (this._allowInvalidTime !== value) {
                this._allowInvalidTime = !!value;
                this._adapter.setHostAttribute(TIME_PICKER_CONSTANTS.attributes.ALLOW_INVALID_TIME, "" + !!value);
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TimePickerFoundation.prototype, "value", {
        get: function () {
            return millisToTimeString(this._value, true, this._allowSeconds);
        },
        set: function (value) {
            if (value && !this._isValidInputValue(value)) {
                this._warnInvalidFormat(value);
                return;
            }
            var millis = this._validateMillis(this._convertTimeStringToMillis(value, true, true));
            this._setValue(millis);
            if (this._isInitialized) {
                this._applyValue(this._value);
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TimePickerFoundation.prototype, "min", {
        get: function () {
            return millisToTimeString(this._min, true, this._allowSeconds);
        },
        set: function (value) {
            if (this._min !== value) {
                if (value && !this._isValidInputValue(value)) {
                    this._warnInvalidFormat(value);
                    return;
                }
                this._min = this._convertTimeStringToMillis(value, true, this._allowSeconds);
                // Validate and reset our millis to ensure within range
                var millis = this._validateMillis(this._value);
                this._setValue(millis);
                if (this._isInitialized) {
                    this._applyValue(millis);
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TimePickerFoundation.prototype, "max", {
        get: function () {
            return millisToTimeString(this._max, true, this._allowSeconds);
        },
        set: function (value) {
            if (this._max !== value) {
                if (value && !this._isValidInputValue(value)) {
                    this._warnInvalidFormat(value);
                    return;
                }
                this._max = this._convertTimeStringToMillis(value, true, this._allowSeconds);
                // Validate and reset our millis to ensure within range
                var millis = this._validateMillis(this._value);
                this._setValue(millis);
                if (this._isInitialized) {
                    this._applyValue(millis);
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TimePickerFoundation.prototype, "restrictedTimes", {
        get: function () {
            var _this = this;
            return this._restrictedTimes
                .map(function (t) { return millisToTimeString(t, true, _this._allowSeconds); })
                .filter(function (v) { return typeof v === 'string'; });
        },
        set: function (value) {
            var _this = this;
            if (!Array.isArray(value)) {
                value = [];
            }
            this._restrictedTimes = value
                .filter(function (v) { return typeof v === 'string'; })
                .map(function (v) { return _this._convertTimeStringToMillis(v, true, true); })
                .filter(function (v) { return typeof v === 'number'; });
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TimePickerFoundation.prototype, "startTime", {
        get: function () {
            return millisToTimeString(this._startTime, true, this._allowSeconds);
        },
        set: function (value) {
            if (this._startTime !== value) {
                if (value && !this._isValidInputValue(value)) {
                    this._warnInvalidFormat(value);
                    return;
                }
                this._startTime = this._convertTimeStringToMillis(value, true, this._allowSeconds);
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TimePickerFoundation.prototype, "step", {
        get: function () {
            return this._step;
        },
        set: function (value) {
            this._step = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TimePickerFoundation.prototype, "allowInput", {
        get: function () {
            return this._allowInput;
        },
        set: function (value) {
            if (this._allowInput !== value) {
                this._allowInput = value;
                if (this._isInitialized) {
                    this._applyAllowInput();
                    this._applyMask();
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TimePickerFoundation.prototype, "popupTarget", {
        get: function () {
            return this._popupTarget;
        },
        set: function (value) {
            if (this._popupTarget !== value) {
                this._popupTarget = value;
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TimePickerFoundation.prototype, "showNow", {
        get: function () {
            return this._showNow;
        },
        set: function (value) {
            if (this._showNow !== value) {
                this._showNow = value;
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TimePickerFoundation.prototype, "customOptions", {
        get: function () {
            return this._customOptions;
        },
        set: function (value) {
            this._customOptions = Array.isArray(value) ? value : [];
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TimePickerFoundation.prototype, "validationCallback", {
        set: function (cb) {
            this._validationCallback = cb;
            if (this._isInitialized) {
                this._applyMask();
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TimePickerFoundation.prototype, "parseCallback", {
        set: function (cb) {
            this._parseCallback = cb;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TimePickerFoundation.prototype, "formatCallback", {
        set: function (cb) {
            this._formatCallback = cb;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TimePickerFoundation.prototype, "coercionCallback", {
        set: function (cb) {
            this._coercionCallback = cb;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TimePickerFoundation.prototype, "prepareMaskCallback", {
        set: function (cb) {
            this._prepareMaskCallback = cb;
            if (this._isInitialized) {
                this._applyMask();
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TimePickerFoundation.prototype, "disabled", {
        get: function () {
            return this._disabled;
        },
        set: function (value) {
            if (this._disabled !== value) {
                this._disabled = !!value;
                if (this._isInitialized) {
                    this._applyDisabled();
                }
                this._adapter.toggleHostAttribute(TIME_PICKER_CONSTANTS.attributes.DISABLED, this._disabled);
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TimePickerFoundation.prototype, "popupClasses", {
        get: function () {
            if (Array.isArray(this._popupClasses)) {
                return __spreadArrays(this._popupClasses);
            }
            return this._popupClasses;
        },
        set: function (value) {
            if (Array.isArray(value)) {
                this._popupClasses = __spreadArrays(value);
            }
            else {
                this._popupClasses = [value];
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TimePickerFoundation.prototype, "dialogWidthThreshold", {
        get: function () {
            return this._dialogWidthThreshold;
        },
        set: function (value) {
            this._dialogWidthThreshold = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TimePickerFoundation.prototype, "allowDropdown", {
        get: function () {
            return this._allowDropdown;
        },
        set: function (value) {
            if (this._allowDropdown !== value) {
                this._allowDropdown = value;
                if (this._isInitialized) {
                    this._applyAllowDropdown();
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    return TimePickerFoundation;
}());
export { TimePickerFoundation };
